// ============================================================
// src/App.js
// ─────────────────────────────────────────────────────────────
// Root component. Handles:
//   • Auth state (logged in / logged out)
//   • Name check — first-time users must set display name
//   • Route switching between LoginPage, NamePage, HuddlePage
// ============================================================

import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabaseClient';

import LoginPage  from './pages/LoginPage';
import NamePage   from './pages/NamePage';
import HuddlePage from './pages/HuddlePage';

import './App.css';

export default function App() {
  const [session,    setSession]    = useState(undefined);
  const [loading,    setLoading]    = useState(true);
  const [needsName,  setNeedsName]  = useState(false);
  const [isFounder,  setIsFounder]  = useState(false);

  useEffect(() => {
    // Safety net — never hang forever
    const timeout = setTimeout(() => setLoading(false), 3000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(timeout);
      setSession(session);
      if (session) checkProfile(session);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        if (session) checkProfile(session);
        else setLoading(false);
        if (window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      }
    );

    return () => { subscription.unsubscribe(); clearTimeout(timeout); };
  }, []);

  async function checkProfile(session) {
    const { data: profile } = await supabase
      .from('profiles')
      .select('username, is_founder')
      .eq('id', session.user.id)
      .single();

    const email = session.user.email || '';
    const emailStub = email.split('@')[0].toLowerCase();
    const username = profile?.username?.toLowerCase() || '';
    const looksAutoGenerated = !profile || username === emailStub;

    setNeedsName(looksAutoGenerated);
    setIsFounder(profile?.is_founder || false);
    setLoading(false);
  }

  function handleNameSaved() {
    setNeedsName(false);
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession({ ...session });
    });
  }

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  // Logged in but needs to set name
  if (session && needsName) {
    return <NamePage session={session} onNameSaved={handleNameSaved} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={session ? <Navigate to="/" replace /> : <LoginPage />}
        />
        <Route
          path="/invite/:token"
          element={
            session
              ? <InviteHandler session={session} />
              : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/"
          element={
            session
              ? <HuddlePage session={session} isFounder={isFounder} />
              : <Navigate to="/login" replace />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function InviteHandler({ session }) {
  const { token } = useParams();
  const navigate  = useNavigate();
  const [status, setStatus] = useState('Joining huddle…');

  useEffect(() => {
    async function joinHuddle() {
      const { data: huddle, error: findError } = await supabase
        .from('huddles')
        .select('id, name')
        .eq('invite_token', token)
        .single();

      if (findError || !huddle) {
        setStatus('Invalid invite link.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const { error: joinError } = await supabase
        .from('huddle_members')
        .insert({ huddle_id: huddle.id, user_id: session.user.id })
        .select()
        .single();

      if (joinError && !joinError.message.includes('duplicate')) {
        setStatus('Could not join. Please try again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      setStatus(`Welcome to ${huddle.name}!`);
      setTimeout(() => navigate('/'), 1500);
    }

    joinHuddle();
  }, [token, session, navigate]);

  return (
    <div className="app-loading">
      <p className="loading-text">{status}</p>
    </div>
  );
}